import {
  getAnalyticsClient,
  getAnalyticsOrganizationId,
  registerDefaultAnalyticsEventProperties,
  trackAnalyticsEvent,
} from './analyticsClient'
import {
  isSafeAnalyticsIdentifier,
  resolveAnalyticsEmailDomain,
  sanitizeAnalyticsProperties,
} from './analyticsPrivacy'
import type {
  AnalyticsProperties,
  AnalyticsPropertyValue,
  AnalyticsUserIdentity,
} from './analyticsTypes'

let identifiedUserId: string | null = null
let signedInEventUserId: string | null = null
let registeredAnalyticsUserProperties: AnalyticsProperties = {}

const persistentAnalyticsUserPropertyNames = [
  'email_domain',
  'organization_id',
  'regional_unit',
  'user_profile',
  'user_role',
] as const

type AnalyticsClient = NonNullable<ReturnType<typeof getAnalyticsClient>>

function resolveUserRole(user: AnalyticsUserIdentity) {
  return user.roles?.[0] ?? undefined
}

function resolveUserProfile(user: AnalyticsUserIdentity) {
  return user.profile ?? user.roles?.slice().sort().join('|') ?? undefined
}

function resolveRegionalUnit(user: AnalyticsUserIdentity) {
  return user.regionalUnit ?? undefined
}

export function buildSafeAnalyticsUserProperties(user: AnalyticsUserIdentity) {
  return sanitizeAnalyticsProperties('user_signed_in', {
    email_domain: resolveAnalyticsEmailDomain(user.email),
    organization_id: user.organizationId || getAnalyticsOrganizationId(),
    regional_unit: resolveRegionalUnit(user),
    user_profile: resolveUserProfile(user),
    user_role: resolveUserRole(user),
  })
}

function clearPersistentAnalyticsUserProperties(analyticsClient: AnalyticsClient) {
  persistentAnalyticsUserPropertyNames.forEach(propertyName => {
    analyticsClient.unregister(propertyName)
  })
}

function registerPersistentAnalyticsUserProperties(
  analyticsClient: AnalyticsClient,
  userProperties: AnalyticsProperties,
) {
  const persistentUserProperties: Record<string, AnalyticsPropertyValue> = {}

  persistentAnalyticsUserPropertyNames.forEach(propertyName => {
    const propertyValue = userProperties[propertyName]
    if (propertyValue !== undefined) {
      persistentUserProperties[propertyName] = propertyValue
    }
  })

  clearPersistentAnalyticsUserProperties(analyticsClient)
  analyticsClient.register(persistentUserProperties)
  registeredAnalyticsUserProperties = userProperties
}

export function identifyAnalyticsUser(user: AnalyticsUserIdentity | null | undefined) {
  const analyticsClient = getAnalyticsClient()
  if (!analyticsClient || !user || !isSafeAnalyticsIdentifier(user.id)) {
    return
  }

  const distinctId = user.id.trim()
  const userProperties = buildSafeAnalyticsUserProperties(user)
  registerPersistentAnalyticsUserProperties(analyticsClient, userProperties)

  if (identifiedUserId !== distinctId) {
    analyticsClient.identify(distinctId, userProperties)
    identifiedUserId = distinctId
  }

  const organizationId = user.organizationId || getAnalyticsOrganizationId()
  if (organizationId) {
    analyticsClient.group('organization', organizationId, {
      organization_id: organizationId,
    })
  }

  if (signedInEventUserId !== distinctId) {
    trackAnalyticsEvent('user_signed_in', userProperties)
    signedInEventUserId = distinctId
  }
}

export function resetAnalyticsUser() {
  const analyticsClient = getAnalyticsClient()
  if (!analyticsClient) {
    return
  }

  trackAnalyticsEvent('user_signed_out', {
    ...registeredAnalyticsUserProperties,
    organization_id: getAnalyticsOrganizationId(),
  })
  clearPersistentAnalyticsUserProperties(analyticsClient)
  analyticsClient.reset()
  registerDefaultAnalyticsEventProperties(analyticsClient)
  registeredAnalyticsUserProperties = {}
  identifiedUserId = null
  signedInEventUserId = null
}
